import React, { useState } from 'react';
import { Target, Users, Search, Workflow, Bot, Layout, ChevronRight, ChevronDown, Activity } from 'lucide-react';

// Service data
const services = [{
  id: 1,
  title: 'PPC Management',
  tagline: 'Target. Bid. Dominate.',
  icon: Target,
  description: 'Our data-driven paid campaigns target high-intent customers with precision, maximizing your ROI across Google, Facebook, and Instagram.',
  example: 'We helped a local gym increase membership sign-ups by 83% while reducing cost-per-acquisition by 22% through strategic Google Ads optimization.'
}, {
  id: 2,
  title: 'Strategy Development',
  tagline: 'Plan. Execute. Scale.',
  icon: Activity,
  description: 'We create comprehensive digital strategies that align with your business goals, mapping out clear paths to growth and market dominance.',
  example: 'Our strategic roadmap helped an e-commerce startup achieve 245% year-over-year growth by identifying key market opportunities and optimization points.'
}, {
  id: 3,
  title: 'Custom AI Agents',
  tagline: 'Automate. Engage. Convert.',
  icon: Bot,
  description: 'We develop tailored AI agents that handle customer interactions, automate tasks, and drive conversions 24/7.',
  example: 'Our custom AI agent for a real estate firm handles 80% of initial client inquiries, qualifying leads and scheduling viewings automatically.'
}, {
  id: 4,
  title: 'Social Media Management',
  tagline: 'Stay visible. Stay top-of-mind.',
  icon: Users,
  description: 'We craft engaging content and manage your social presence across platforms, building community and driving consistent engagement.',
  example: 'Our client, a boutique bakery, saw 3x follower growth and a 67% increase in direct message orders through our strategic content calendar and community management.'
}, {
  id: 5,
  title: 'SEO & Content Marketing',
  tagline: 'Rank higher. Build trust. Be found.',
  icon: Search,
  description: 'Our technical SEO and content strategies help your business climb search rankings and establish authority in your industry.',
  example: 'We helped a local law firm rank on page 1 for their top 5 target keywords, resulting in a 125% increase in qualified organic leads within 6 months.'
}, {
  id: 6,
  title: 'CRM & Automation',
  tagline: 'Capture leads. Strike automatically.',
  icon: Workflow,
  description: 'We implement smart systems that nurture leads, automate follow-ups, and turn inquiries into loyal customers—even while you sleep.',
  example: 'Our automated email sequence for a real estate client achieved a 31% conversion rate from inquiry to showing, saving 15+ hours of manual follow-up weekly.'
}];
const ServicesSection = () => {
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const toggleService = (id: number) => {
    setExpandedService(expandedService === id ? null : id);
  };
  return <section id="services" className="section-container">
      <h2 className="section-title">
        More Than a Website. <span className="text-goldcore">We Grow Brands.</span>
      </h2>
      <p className="text-center text-deepshadow max-w-3xl mx-auto mb-16 text-lg">A website is just the beginning. Our suite of marketing services help you scale your business to new heights.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map(service => {
        const Icon = service.icon;
        const isExpanded = expandedService === service.id;
        return <div key={service.id} className={`service-card cursor-pointer group ${isExpanded ? 'bg-knightblack text-softwhite border-goldcore' : ''}`} onClick={() => toggleService(service.id)}>
              <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-full ${isExpanded ? 'bg-goldcore text-knightblack' : 'bg-goldcore/10 text-goldcore'} group-hover:bg-goldcore group-hover:text-knightblack transition-colors`}>
                  <Icon size={24} />
                </div>
                <button className="text-goldcore">
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </button>
              </div>
              
              <h3 className={`text-xl font-bold mb-2 ${isExpanded ? 'text-goldcore' : 'group-hover:text-goldcore transition-colors'}`}>
                {service.title}
              </h3>
              
              <p className={`font-medium mb-4 ${isExpanded ? 'text-softwhite/90' : ''}`}>
                {service.tagline}
              </p>
              
              {isExpanded && <div className="mt-4 space-y-4 animate-fade-in">
                  <p className="text-softwhite/80">{service.description}</p>
                  <div className="bg-goldcore/10 p-4 rounded border-l-2 border-goldcore">
                    <p className="text-sm text-softwhite italic">{service.example}</p>
                  </div>
                </div>}
            </div>;
      })}
      </div>
    </section>;
};
export default ServicesSection;